import styled, { css } from 'styled-components';
import tinycolor from 'tinycolor2';

/**
 * Kiosk Pro breakpoints - optimized for kiosk hardware
 */
const KIOSK_PRO_LANDSCAPE_BREAKPOINT = 1510;
const KIOSK_PRO_PORTRAIT_BREAKPOINT = 720;
/** Max landscape height for compact kiosk layout (e.g. 1024×768, 1112×834 iPad Pro 10.5"). */
export const KIOSK_PRO_SMALL_LANDSCAPE_BREAKPOINT = 840;
/** Max portrait height for compact kiosk layout (e.g. 720×1280, 768×1024, 820×1180, 834×1112). */
export const KIOSK_PRO_SMALL_PORTRAIT_BREAKPOINT = 1180;
/** Max width to distinguish small landscape (e.g. 1024×768) from medium landscape (e.g. 1334×800). */
const KIOSK_PRO_SMALL_LANDSCAPE_WIDTH_BREAKPOINT = 1100;

export const KIOSK_PRO_MEDIA = {
  maxWidth: `only screen and (max-width: ${KIOSK_PRO_LANDSCAPE_BREAKPOINT}px)`,
  landscapeMaxWidth: `only screen and (orientation: landscape) and (max-width: ${KIOSK_PRO_LANDSCAPE_BREAKPOINT}px)`,
  portraitMaxWidth: `only screen and (orientation: portrait) and (max-width: ${KIOSK_PRO_PORTRAIT_BREAKPOINT}px)`,
  /** Portrait + limited height — layout uses vw/vh/vmin so proportions stay close to large portrait */
  smallViewportPortrait: `only screen and (orientation: portrait) and (max-height: ${KIOSK_PRO_SMALL_PORTRAIT_BREAKPOINT}px)`,
  /** Landscape + limited height: matches both medium (1334×800) and small (1024×768) landscape */
  smallViewportLandscape: `only screen and (orientation: landscape) and (max-height: ${KIOSK_PRO_SMALL_LANDSCAPE_BREAKPOINT}px)`,
  /** Landscape + limited height + limited width: only matches small landscape (e.g. 1024×768), not medium (1334×800) */
  smallViewportLandscapeOnly: `only screen and (orientation: landscape) and (max-height: ${KIOSK_PRO_SMALL_LANDSCAPE_BREAKPOINT}px) and (max-width: ${KIOSK_PRO_SMALL_LANDSCAPE_WIDTH_BREAKPOINT}px)`,
};

/**
 * Touch-target hardening applied to interactive elements (links/buttons)
 * across Kiosk Pro:
 * - `-webkit-tap-highlight-color: transparent` suppresses the tap highlight
 *   flash.
 * - `touch-action: manipulation` disables double-tap-to-zoom, removing its
 *   click delay.
 * - `user-select: none` prevents text selection/callout on long-press.
 */
export const kioskTouchHints = css`
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
`;

export const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100svh;
  max-width: 1600px;
  margin: 0 auto;
  place-items: center;
  text-align: center;

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    align-content: start;
    justify-items: center;
    align-items: start;
  }

  @media (orientation: landscape) {
    grid-template-columns: minmax(340px, auto) 1fr;
    gap: 2rem;
  }
`;

export const PageContainerBackground = styled.div`
  position: relative;
  background-color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100svh;

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscape} {
    overflow: hidden;
    max-width: 100vw;
  }
`;

export const Title = styled.h1`
  font-size: 56px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 36px;
    margin: 0;
    line-height: 1.2;
    text-align: center;
  }
`;

/** Card headings on login options (guest column + login column) */
export const LoginOptionsTitle = styled.h3`
  font-size: 36px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscape} {
    font-size: 24px;
  }
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.grey[40]};
  font-size: 36px;
  font-weight: 700;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 24px;
  }
`;
